import { useCallback, useEffect, useState } from "react";
import {
  MAX_ATTACHMENT_BYTES,
  SCREENSHOT_EXCLUDE_ATTR,
} from "./FeedbackDialog.constants";

export type ScreenshotCapture = {
  /** Resolves to a JPEG data URL, or null if cancelled / unsupported / failed. */
  capture: () => Promise<string | null>;
  capturing: boolean;
};

/**
 * Result of reading a user-supplied image (upload / paste). Distinguishes the
 * oversized case so the UI can surface a "too large" message rather than
 * silently dropping the attachment.
 */
export type ImageReadResult =
  | { ok: true; src: string }
  | { ok: false; reason: "not-image" | "too-large" | "error" };

// Screenshot via MediaDevices.getDisplayMedia: instant and pixel-perfect (grabs
// already-painted compositor output, so it reflects scroll/charts/canvas for
// free). Cost: one browser picker prompt.

const NATIVE_SUPPORTED =
  typeof navigator !== "undefined" && !!navigator.mediaDevices?.getDisplayMedia;

const MAX_CAPTURE_WIDTH = 1920;

const scaleToMax = (
  width: number,
  height: number
): { width: number; height: number } => {
  if (width <= MAX_CAPTURE_WIDTH) {
    return { width, height };
  }
  const scale = MAX_CAPTURE_WIDTH / width;
  return { width: MAX_CAPTURE_WIDTH, height: Math.round(height * scale) };
};

/**
 * Native capture grabs live tab pixels, so the feedback modal/FAB would appear
 * in the shot. Hide every marked overlay, returning a restore fn. visibility
 * (not display) avoids reflowing the page behind them.
 */
const hideMarkedOverlays = (): (() => void) => {
  const overlays = Array.from(
    document.querySelectorAll<HTMLElement>(`[${SCREENSHOT_EXCLUDE_ATTR}]`)
  );
  const previous = overlays.map((el) => el.style.visibility);
  for (const el of overlays) {
    el.style.visibility = "hidden";
  }
  return () => {
    overlays.forEach((el, i) => {
      el.style.visibility = previous[i];
    });
  };
};

/** Wait long enough for the hidden state to be composited into the stream. */
const waitForRepaint = (): Promise<void> =>
  new Promise((resolve) => {
    requestAnimationFrame(() =>
      requestAnimationFrame(() => setTimeout(resolve, 50))
    );
  });

export const useScreenshotCapture = (): ScreenshotCapture => {
  const [capturing, setCapturing] = useState(false);

  const capture = useCallback(async (): Promise<string | null> => {
    if (!NATIVE_SUPPORTED || capturing) {
      return null;
    }

    setCapturing(true);
    let stream: MediaStream | undefined;
    // Hide the modal/FAB up front — before the permission dialog opens — so they
    // are already gone while the prompt is up and when the frame is grabbed.
    // Hiding only after the user clicks Allow is too late: the captured frame
    // still shows the modal.
    const restoreOverlays = hideMarkedOverlays();

    try {
      // `preferCurrentTab` is a non-standard Chromium hint that surfaces this
      // tab as the picker's primary choice (otherwise Chrome may not offer the
      // current tab at all). Not in the DOM lib types, hence the cast.
      const displayMediaOptions: DisplayMediaStreamOptions & {
        preferCurrentTab?: boolean;
      } = { video: true, preferCurrentTab: true };
      stream = await navigator.mediaDevices.getDisplayMedia(displayMediaOptions);
      const video = document.createElement("video");
      video.srcObject = stream;
      video.muted = true;

      await new Promise<void>((resolve, reject) => {
        video.onloadedmetadata = () => resolve();
        video.onerror = () => reject(video.error);
      });
      await video.play();
      await waitForRepaint();

      const { width, height } = scaleToMax(video.videoWidth, video.videoHeight);
      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      canvas.getContext("2d")?.drawImage(video, 0, 0, width, height);

      // JPEG (not PNG): a 1920-wide UI screenshot is ~10x smaller as JPEG, so a
      // capture comfortably stays under the attachment size cap and the support
      // email stays well within inbox limits. Quality 0.9 keeps text legible.
      return canvas.toDataURL("image/jpeg", 0.9);
    } catch {
      // Cancelled picker (NotAllowedError) is a silent no-op.
      return null;
    } finally {
      stream?.getTracks().forEach((track) => track.stop());
      restoreOverlays();
      setCapturing(false);
    }
  }, [capturing]);

  return { capture, capturing };
};

// --- Clipboard / paste -----------------------------------------------------

/** Read an image Blob/File into a data URL, bounding the size up front. */
export const readImageAsDataUrl = (blob: Blob): Promise<ImageReadResult> => {
  if (!blob.type.startsWith("image/")) {
    return Promise.resolve({ ok: false, reason: "not-image" });
  }
  // Reject oversized images before reading so the user keeps their typed
  // feedback (the backend would otherwise drop the screenshot or reject the
  // whole submission). blob.size is the decoded byte count, matching the cap.
  if (blob.size > MAX_ATTACHMENT_BYTES) {
    return Promise.resolve({ ok: false, reason: "too-large" });
  }
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = () =>
      resolve(
        typeof reader.result === "string"
          ? { ok: true, src: reader.result }
          : { ok: false, reason: "error" }
      );
    reader.onerror = () => resolve({ ok: false, reason: "error" });
    reader.readAsDataURL(blob);
  });
};

/**
 * Fire `onResult` when the user pastes an image (Ctrl/Cmd+V) anywhere.
 * Mounted only while the dialog is open, so the document listener is scoped to
 * that lifetime. `onResult` must be stable (wrap in useCallback).
 */
export const usePasteImage = (
  onResult: (result: ImageReadResult, name?: string) => void
): void => {
  useEffect(() => {
    const handlePaste = async (event: ClipboardEvent) => {
      const items = event.clipboardData?.items;
      if (!items) {
        return;
      }
      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (item.type.startsWith("image/")) {
          const file = item.getAsFile();
          if (file) {
            onResult(await readImageAsDataUrl(file), file.name || undefined);
          }
          return; // first image only
        }
      }
    };

    document.addEventListener("paste", handlePaste);
    return () => document.removeEventListener("paste", handlePaste);
  }, [onResult]);
};

/**
 * Read the first image from the async Clipboard API (button-triggered).
 * Returns null when the clipboard holds no image; otherwise the read result
 * (which may itself be a "too-large" failure).
 */
export const readClipboardImage = async (): Promise<ImageReadResult | null> => {
  if (!navigator.clipboard?.read) {
    return null;
  }
  try {
    const items = await navigator.clipboard.read();
    for (const item of items) {
      const imageType = item.types.find((type) => type.startsWith("image/"));
      if (imageType) {
        const blob = await item.getType(imageType);
        return await readImageAsDataUrl(blob);
      }
    }
    return null;
  } catch {
    return null;
  }
};
